import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
// @material-ui/core components
import {
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Typography,
  Avatar,
  Divider,
  Fade,
  Grow,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

// @material-ui/icons
import PermIdentity from "@material-ui/icons/PermIdentity";
import Timeline from "@material-ui/icons/Timeline";
import Email from "@material-ui/icons/Email";
import Phone from "@material-ui/icons/Phone";
import VpnKey from "@material-ui/icons/VpnKey";
import People from "@material-ui/icons/People";
import Launch from "@material-ui/icons/Launch";

// Amplify components
import { Auth } from "aws-amplify";

// core components
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Button from "components/CustomButtons/Button.js";
import CustomInput from "components/CustomInput/CustomInput.js";
import Clearfix from "components/Clearfix/Clearfix.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardIcon from "components/Card/CardIcon.js";
import CardBody from "components/Card/CardBody.js";
import CardAvatar from "components/Card/CardAvatar.js";
import Snackbar from "components/Snackbar/Snackbar.js";

import styles from "assets/jss/material-dashboard-pro-react/views/userProfileStyles.js";

import avatar from "assets/img/faces/generic-user-icon-19.jpg";
import { AddAlert } from "@material-ui/icons";
import { fetchAllUsersAPI, updateUserCategoryAPI } from "../APIs/APIUtility";
import { applyCodeToUserAPI } from "../APIs/ToolboxUtility";
import ReactTable from "components/ReactTable/ReactTable";
import WaveTitle from "views/Dashboard/SolutionGenerator/WaveTitle";

const useStyles = makeStyles((theme) => ({
  ...styles,
  container: {
    padding: theme.spacing(2),
    // Override all input focus colors globally
    "& .MuiInput-underline:after": {
      borderBottomColor: "#20677b !important",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#20677b !important",
    },
    "& .MuiFormLabel-root.Mui-focused": {
      color: "#20677b !important",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#20677b !important",
    },
  },
  glassCard: {
    background: "rgba(255,255,255,0.95)",
    backdropFilter: "blur(10px)",
    border: "2px solid #20677b",
    borderRadius: 8,
    padding: theme.spacing(2.5),
    transition: "all 0.2s ease",
    marginBottom: theme.spacing(2),
    "&:hover": {
      border: "2px solid #20677b",
    },
  },
  profileHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(2.5),
    background: "rgba(255,255,255,0.95)",
    backdropFilter: "blur(10px)",
    border: "2px solid #20677b",
    borderRadius: 8,
    marginBottom: theme.spacing(2),
    color: "#333333",
  },
  largeAvatar: {
    width: 80,
    height: 80,
    border: "2px solid rgba(32, 103, 123, 0.6)",
  },
  profileInfo: {
    flex: 1,
    marginLeft: theme.spacing(2),
  },
  profileName: {
    fontSize: "1.5rem",
    fontWeight: 600,
    color: "#1a1a1a",
    marginBottom: 4,
  },
  profileEmail: {
    fontSize: "0.9rem",
    color: "#555555",
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginBottom: 2,
  },
  sectionTitle: {
    fontSize: "1rem",
    fontWeight: 600,
    color: "#1a1a1a",
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      color: "#1a1a1a",
      backgroundColor: "rgba(255, 255, 255, 0.8)",
      "& fieldset": {
        borderColor: "rgba(0, 0, 0, 0.23)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(0, 0, 0, 0.4)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#20677b !important",
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-root": {
      color: "rgba(0, 0, 0, 0.6)",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#20677b !important",
    },
    "& .MuiInputBase-input.Mui-disabled": {
      color: "rgba(0, 0, 0, 0.5)",
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "#20677b !important",
    },
  },
  actionButton: {
    backgroundColor: "#20677b",
    color: "#ffffff",
    border: "1px solid #20677b",
    borderRadius: 6,
    padding: theme.spacing(1, 3),
    transition: "all 0.2s ease",
    fontWeight: 500,
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#165d64",
      border: "1px solid #165d64",
    },
    "&:disabled": {
      backgroundColor: "rgba(0, 0, 0, 0.12)",
      color: "rgba(0, 0, 0, 0.26)",
    },
  },
  quickActionCard: {
    background: "rgba(255,255,255,0.95)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(32, 103, 123, 0.3)",
    borderRadius: 8,
    padding: theme.spacing(2.5),
    cursor: "pointer",
    transition: "all 0.2s ease",
    color: "#333333",
    textAlign: "center",
    "&:hover": {
      border: "1px solid rgba(32, 103, 123, 0.5)",
      background: "rgba(32, 103, 123, 0.1)",
    },
  },
  infoRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    color: "#555555",
    fontSize: "0.875rem",
  },
  footer: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    background: "rgba(255, 255, 255, 0.9)",
    borderRadius: 6,
    border: "2px solid #20677b",
    fontSize: "0.8rem",
    color: "#1a1a1a",
    fontStyle: "italic",
    lineHeight: 1.5,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    "& a": {
      color: "#20677b",
      textDecoration: "none",
      fontWeight: 500,
      "&:hover": {
        color: "#165d64",
        textDecoration: "underline",
      },
    },
  },
  tableWrapper: {
    background: "#ffffff",
    borderRadius: 6,
    padding: 16,
    marginTop: 8,
    border: "1px solid rgba(0, 0, 0, 0.1)",
    "& table": {
      color: "#1a1a1a",
    },
    "& th": {
      color: "#1a1a1a",
      fontWeight: 600,
    },
    "& td": {
      color: "#333333",
    },
  },
}));

export default function UserProfile() {
  const classes = useStyles();
  const history = useHistory();
  const [user, setUser] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [phone, setPhone] = React.useState("");
  // const [preferredUsername, setpreferredUsername] = React.useState("");
  const [br, setBR] = React.useState(false);
  const [message, setmessage] = React.useState("Your profile is updated.");
  const [status, setstatus] = React.useState("success");
  const [quUsers, setQuUsers] = React.useState([]);
  const [showTable, setShowTable] = React.useState(false);
  const [loading, setLoading] = useState({ isLoading: false, message: "" });
  const [accessCode, setAccessCode] = React.useState("");

  // Define table columns
  const columns = useMemo(
    () => [
      { Header: "Email", accessor: "email" },
      { Header: "First Name", accessor: "first_name" },
      { Header: "Last Name", accessor: "last_name" },
      { Header: "Registration Date", accessor: "registration_date" },
      // { Header: "Category", accessor: "category" },
      {
        Header: "Category",
        accessor: "category",
        Cell: ({ row }) =>
          row.original.category === "super_admin" ? (
            <span>Super Admin</span> // Display text if super_admin
          ) : (
            <Select
              value={row.original.category}
              onChange={(e) =>
                updateCategory(row.original.username, e.target.value)
              }
            >
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="super_admin">Super Admin</MenuItem>
            </Select>
          ),
      },
    ],
    []
  );

  const applyAccessCode = async () => {
    try {
      setLoading({ isLoading: true, message: "Applying access code..." });
      const formData = new FormData();
      formData.append("code", accessCode);
      const res = await applyCodeToUserAPI(formData);
      if (res.status === 200) {
        setmessage("Access code applied successfully.");
        setstatus("quSandBoxColor");
        showNotification("br");
        setAccessCode("");
      } else {
        setmessage(res.error);
        setstatus("danger");
        showNotification("br");
      }
    } catch (err) {
      console.error("Error applying access code: ", err);
      setmessage(err.error);
      setstatus("danger");
      showNotification("br");
    } finally {
      setLoading({ isLoading: false, message: "" });
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        setUser(user);
        setEmail(user.attributes.email);
        setFirstName(user.attributes.given_name);
        setLastName(user.attributes.family_name);
        setPhone(user.attributes.phone_number);
        const quCreateUsers = await fetchAllUsersAPI();

        quCreateUsers.forEach((quCreateUser) => {
          if (
            quCreateUser.username === user.username &&
            quCreateUser.category === "super_admin"
          ) {
            setShowTable(true);
            setQuUsers(quCreateUsers);
            return;
          }
        });
      } catch (err) {
        console.error("Error fetching user data: ", err);
        history.push("/auth/login-page");
      }
    };

    fetchUserData();
  }, [history, setUser, setEmail, setFirstName, setLastName, setPhone]);

  const updateProfile = async () => {
    let result = await Auth.updateUserAttributes(user, {
      given_name: firstName,
      family_name: lastName,
      phone_number: phone,
      // 'preferred_username': preferredUsername
    });
    if (result === "SUCCESS") {
      setstatus("quSandBoxColor");
      setmessage("SUCCESS: Your profile is updated.");
      showNotification("br");
    } else {
      setstatus("danger");
      setmessage("FAILED: Retry or Contact info@qusandbox.com for help.");
      showNotification("br");
    }
  };

  const showNotification = (place) => {
    setBR(true);
    setTimeout(function () {
      setBR(false);
    }, 6000);
  };

  const updateCategory = async (username, newCategory) => {
    try {
      setLoading({ isLoading: true, message: "Updating user category..." });
      const formData = new FormData();
      formData.append("username", username);
      formData.append("category", newCategory);
      const res = await updateUserCategoryAPI(formData);
      setQuUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.username === username ? { ...user, category: newCategory } : user
        )
      );
    } catch (err) {
      console.error("Error updating user category: ", err);
    } finally {
      setLoading({ isLoading: false, message: "" });
    }
  };

  if (loading.isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress color="quCreateColor" />
        <Typography
          variant="h6"
          style={{ marginLeft: 16, textTransform: "none" }}
        >
          {loading.message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Divider style={{ marginBottom: 16 }} />
      <Typography variant="h4">
        <WaveTitle text="User Profile" />
      </Typography>
      <Typography variant="body1">
        Manage your account information and settings below.
      </Typography>
      <Divider style={{ marginTop: 16, marginBottom: 16 }} />
      <Snackbar
        place="br"
        color={status}
        message={message}
        icon={AddAlert}
        open={br}
        closeNotification={() => setBR(false)}
        close
      />

      <GridContainer spacing={2}>
        {/* Profile Header Card */}
        <GridItem xs={12}>
          <Box className={classes.profileHeader}>
            <Avatar
              src={avatar}
              alt={firstName}
              className={classes.largeAvatar}
            />
            <Box className={classes.profileInfo}>
              <Typography className={classes.profileName}>
                {firstName} {lastName}
              </Typography>
              <Typography className={classes.profileEmail}>
                <Email fontSize="small" style={{ marginRight: 4 }} />
                {email}
              </Typography>
              {phone && (
                <Typography className={classes.infoRow}>
                  <Phone fontSize="small" style={{ marginRight: 4 }} />
                  {phone}
                </Typography>
              )}
            </Box>
            {showTable && (
              <Button
                className={classes.actionButton}
                endIcon={<Launch />}
                onClick={() => {
                  window.location.href = "/create/prompt-hub";
                }}
              >
                PromptHub
              </Button>
            )}
          </Box>
        </GridItem>

        {/* Edit Profile Section */}
        <GridItem xs={12}>
          <Box className={classes.glassCard}>
            <Typography className={classes.sectionTitle}>
              <PermIdentity fontSize="small" style={{ marginRight: 4 }} />
              Edit Profile
            </Typography>

            <GridContainer spacing={2}>
              <GridItem xs={12} sm={6}>
                <CustomInput
                  labelText="Email Address"
                  disabled
                  value={email}
                  id="username"
                  formControlProps={{
                    fullWidth: true,
                    className: classes.textField,
                  }}
                  inputProps={{
                    disabled: true,
                  }}
                />
              </GridItem>

              <GridItem xs={12} sm={6}>
                <CustomInput
                  labelText="Phone Number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  id="phone-number"
                  formControlProps={{
                    fullWidth: true,
                    className: classes.textField,
                  }}
                />
              </GridItem>

              <GridItem xs={12} sm={6}>
                <CustomInput
                  labelText="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  id="first-name"
                  formControlProps={{
                    fullWidth: true,
                    className: classes.textField,
                  }}
                />
              </GridItem>

              <GridItem xs={12} sm={6}>
                <CustomInput
                  labelText="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  id="last-name"
                  formControlProps={{
                    fullWidth: true,
                    className: classes.textField,
                  }}
                />
              </GridItem>
            </GridContainer>

            <Box
              style={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button className={classes.actionButton} onClick={updateProfile}>
                Update Profile
              </Button>
            </Box>
          </Box>
        </GridItem>

        {/* Access Code Section */}
        <GridItem xs={12}>
          <Box className={classes.glassCard}>
            <Typography className={classes.sectionTitle}>
              <VpnKey fontSize="small" style={{ marginRight: 4 }} />
              Toolbox Access Code
            </Typography>

            <Typography
              variant="body2"
              style={{
                color: "#555555",
                fontSize: "0.875rem",
              }}
            >
              Enter your access code to unlock premium toolboxes. Contact{" "}
              <strong style={{ color: "#1a1a1a" }}>info@qusandbox.com</strong>{" "}
              for assistance.
            </Typography>

            <GridContainer spacing={2} alignItems="flex-end">
              <GridItem xs={12} sm={8}>
                <CustomInput
                  labelText="Access Code"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  id="access-code"
                  formControlProps={{
                    fullWidth: true,
                    className: classes.textField,
                  }}
                />
              </GridItem>
              <GridItem xs={12} sm={4}>
                <Button
                  className={classes.actionButton}
                  onClick={applyAccessCode}
                  style={{ width: "100%" }}
                >
                  Apply Code
                </Button>
              </GridItem>
            </GridContainer>
          </Box>
        </GridItem>

        {/* Admin User Management Section */}
        {showTable && (
          <GridItem xs={12}>
            <Box className={classes.glassCard}>
              <Typography className={classes.sectionTitle}>
                <People fontSize="small" style={{ marginRight: 4 }} />
                User Management
              </Typography>
              <Box className={classes.tableWrapper}>
                <ReactTable columns={columns} data={quUsers} />
              </Box>
            </Box>
          </GridItem>
        )}
      </GridContainer>

      {/* Footer */}
      <Box className={classes.footer}>
        <Box style={{ flex: 1 }}>
          By logging in, you agree to the{" "}
          <a
            href="https://www.quantuniversity.com/privacy.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>{" "}
          and{" "}
          <a href="/terms" target="_blank" rel="noopener noreferrer">
            Terms and Conditions
          </a>{" "}
          of QuantUniversity LLC.
        </Box>
      </Box>
    </Box>
  );
}
